/**
 * Efectos del reproductor PEFF: audio modelo, micrófono, subida a /v1/media y
 * avance de la batería en /v1/assessment. Toda regla de transición vive en el
 * reducer (peff-player.ts); aquí solo se ejecutan las consecuencias.
 *
 * Contrato con el API:
 *  - Los ejercicios adaptativos (F3/PSF2) materializan sus ítems al iniciarse,
 *    así que tras `start` se recarga la batería para leerlos.
 *  - Cada ítem se registra UNA vez, al avanzar, con su audioRef y sus flags
 *    (withHint / noEvoca / sinIntento / dudoso). Así el backend ve exactamente
 *    lo administrado, incluidos los ítems sin intento (racha de suspensión).
 *  - La suspensión (del adulto o automática por racha) hace POST /suspend, que
 *    anula el intento del ejercicio en curso; al reanudar se administra entero.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { bateriaApi, mediaApi, wordBankApi } from "../api/endpoints.js";
import { putSignedUrl } from "../api/client.js";
import { mensajeDeError } from "../hooks/useQuery.js";
import type { BateriaCompletaDTO, PeffApoyo, RegistroItem } from "../api/types.js";
import { bateriaEnCierre, ejercicioYaCerrado, itemsDe, planSesion, wordIdsDe } from "./bateria.js";
import { reproducir, type Locucion } from "./audio.js";
import { ESPERA_DENOMINACION_MS, MECANICA } from "./mecanica.js";
import { CONFIG_VOZ_F1 } from "./vad.js";
import {
  ejercicioActual,
  esUltimoItem,
  initPlayer,
  itemActual,
  mecanicaActual,
  playerReducer,
  type EstadoPlayer,
  type InitPlayerInput,
} from "./peff-player.js";
import { useRecorder } from "./useRecorder.js";

export interface PeffPlayerTextos {
  /** Locución de la pista semántica cuando word-bank no la tiene (placeholder). */
  pistaPlaceholder: string;
  /** Pregunta distractora por defecto (placeholder). */
  distractoraPlaceholder: string;
  /** "Ahora dila tú" (PSF2). */
  ahoraTu: string;
}

export function usePeffPlayer(
  bateria: BateriaCompletaDTO,
  plan: InitPlayerInput,
  textos: PeffPlayerTextos,
) {
  const [state, dispatch] = useReducer(playerReducer, plan, initPlayer);
  const recorder = useRecorder();
  const blobRef = useRef<Blob | null>(null);
  const audioRefRef = useRef<string | null>(null);
  const suspendidoRef = useRef(false);
  // Guarda de "en vuelo" del start: sin ella, cada toque del adulto en la intro
  // dispara un POST /start; el backend ya lo hace idempotente (200), pero la
  // guarda evita la ráfaga de requests idénticos y permite deshabilitar el botón.
  const iniciandoRef = useRef(false);
  const [iniciando, setIniciando] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  const bateriaId = bateria.id;
  const childId = bateria.childId;

  // ---- registro del ítem en el API ---------------------------------------
  const registrarItem = useCallback(async (s: EstadoPlayer) => {
    const item = itemActual(s);
    if (!item) return;
    const body: RegistroItem = {
      ...(audioRefRef.current !== null ? { audioRef: audioRefRef.current } : {}),
      withHint: s.flags.withHint,
      noEvoca: s.flags.noEvoca,
      sinIntento: s.flags.sinIntento,
      dudoso: s.flags.dudoso,
      revisarAudio: s.flags.revisarAudio,
    };
    await bateriaApi.registrarItem(item.id, body);
  }, []);

  // ---- grabación ----------------------------------------------------------
  const subir = useCallback(async () => {
    const s = stateRef.current;
    const item = itemActual(s);
    const blob = blobRef.current;
    if (!item || !blob) return;
    dispatch({ tipo: "SUBIR_INICIO" });
    try {
      const subida = await mediaApi.urlSubidaAudio({
        childId,
        sesionId: bateriaId,
        palabraId: item.id,
        contentType: recorder.contentType,
      });
      await putSignedUrl(subida.urlSubida, blob, subida.headers);
      audioRefRef.current = subida.audioRef;
      dispatch({ tipo: "SUBIR_OK" });
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
    }
  }, [bateriaId, childId, recorder.contentType]);

  const grabarFin = useCallback(async () => {
    if (!recorder.grabando) return;
    try {
      blobRef.current = await recorder.detener();
      dispatch({ tipo: "GRABAR_FIN" });
      await subir();
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
    }
  }, [recorder, subir]);

  const grabarInicio = useCallback(
    async (autoStop: boolean) => {
      try {
        dispatch({ tipo: "GRABAR_INICIO" });
        await recorder.iniciar(
          autoStop ? { autoStop: CONFIG_VOZ_F1, onCorte: () => void grabarFin() } : undefined,
        );
      } catch (e) {
        dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
      }
    },
    [recorder, grabarFin],
  );

  // ---- avance -------------------------------------------------------------
  const avanzar = useCallback(async () => {
    const s = stateRef.current;
    const ejercicio = ejercicioActual(s);
    if (!ejercicio) return;
    const eraUltimo = esUltimoItem(s);
    const siguiente = playerReducer(s, { tipo: "SIGUIENTE_ITEM" });
    if (siguiente === s) return;

    try {
      await registrarItem(s);
      // La suspensión automática necesita los flags ya persistidos: el backend
      // recuenta la racha de `sin_intento` sobre los ítems del ejercicio.
      if (siguiente.estado === "suspendida") {
        await bateriaApi.suspender(bateriaId, "SIN_INTENTO");
        suspendidoRef.current = true;
      } else if (eraUltimo) {
        await bateriaApi.completarEjercicio(ejercicio.id);
      }
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
      return;
    }
    audioRefRef.current = null;
    blobRef.current = null;
    dispatch({ tipo: "SIGUIENTE_ITEM" });
  }, [bateriaId, registrarItem]);

  // ---- ciclo de vida de la batería ---------------------------------------
  const micOk = useCallback(async () => {
    try {
      await bateriaApi.micCheck(bateriaId);
      dispatch({ tipo: "MIC_OK" });
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
    }
  }, [bateriaId]);

  const iniciarEjercicio = useCallback(async () => {
    const ejercicio = ejercicioActual(stateRef.current);
    if (!ejercicio) return;
    // Un solo intento por entrada a la intro: si ya hay un start en vuelo,
    // ignoramos los toques extra (evita la ráfaga de POST /start).
    if (iniciandoRef.current) return;
    iniciandoRef.current = true;
    setIniciando(true);
    try {
      // El backend es idempotente: si el ejercicio ya estaba en_progreso
      // (doble entrada, reintento), responde 200 con su estado y continuamos
      // con normalidad recargando los ítems.
      await bateriaApi.iniciarEjercicio(ejercicio.id);
      // F3/PSF2 solo tienen ítems después de iniciarse (adaptativo).
      const fresca = await bateriaApi.obtener(bateriaId);
      const apoyos = await cargarApoyos(fresca);
      const items = itemsDe(fresca, ejercicio.id, apoyos);
      // Precarga por lote (conexión inestable): baja imagen+audio de todos los
      // ítems del ejercicio al entrar, para que el render por ítem ya esté en
      // caché. Best-effort; nunca bloquea ni lanza.
      precargarMedios(items);
      dispatch({ tipo: "INICIAR_EJERCICIO", items });
    } catch (e) {
      // El `start` puede fallar con 409 no por un error transitorio, sino porque
      // el estado ya avanzó: un adaptativo (F3/PSF2) sin ítems elegibles se
      // descarta al cerrar su denominación, y descartar el ÚLTIMO cierra la
      // batería ('completada'→'procesando'). Reintentar el start solo produce
      // una ráfaga de 409. Releemos el estado real: si el ejercicio ya es
      // terminal o la batería ya no admite administración, lo saltamos con el
      // cierre por ítems vacíos (→ pausa o → procesamiento), no lo reintentamos.
      const fresca = await bateriaApi.obtener(bateriaId).catch(() => null);
      if (fresca && (bateriaEnCierre(fresca) || ejercicioYaCerrado(fresca, ejercicio.id))) {
        dispatch({ tipo: "INICIAR_EJERCICIO", items: [] });
      } else {
        dispatch({ tipo: "INICIAR_EJERCICIO_ERROR", mensaje: mensajeDeError(e) });
      }
    } finally {
      iniciandoRef.current = false;
      setIniciando(false);
    }
  }, [bateriaId]);

  const suspender = useCallback(async () => {
    try {
      await bateriaApi.suspender(bateriaId, "SENAL_ADULTO");
      suspendidoRef.current = true;
      dispatch({ tipo: "SUSPENDER", motivo: "SENAL_ADULTO" });
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
    }
  }, [bateriaId]);

  const reanudar = useCallback(async () => {
    try {
      await bateriaApi.reanudar(bateriaId);
      const fresca = await bateriaApi.obtener(bateriaId);
      const apoyos = await cargarApoyos(fresca);
      const nuevoPlan = planSesion(fresca, apoyos);
      suspendidoRef.current = false;
      dispatch({
        tipo: "REANUDAR",
        plan: { ...nuevoPlan, micCheckHecho: true },
      });
    } catch (e) {
      dispatch({ tipo: "SUBIR_ERROR", mensaje: mensajeDeError(e) });
    }
  }, [bateriaId]);

  const procesar = useCallback(async () => {
    dispatch({ tipo: "PROCESAR" });
    try {
      await bateriaApi.procesar(bateriaId);
    } catch {
      // El pre-informe se resuelve del lado del servidor; si el procesamiento
      // asíncrono falla, el cierre igual se muestra y el estado real vive en la
      // batería. Nunca dejamos al niño en una pantalla de espera infinita.
    }
    dispatch({ tipo: "COMPLETADO" });
  }, [bateriaId]);

  const siguienteEjercicio = useCallback(() => dispatch({ tipo: "SIGUIENTE_EJERCICIO" }), []);
  const regrabar = useCallback(() => {
    blobRef.current = null;
    audioRefRef.current = null;
    dispatch({ tipo: "REGRABAR" });
  }, []);
  const marcarSinIntento = useCallback(() => dispatch({ tipo: "MARCAR_SIN_INTENTO" }), []);
  const marcarDudoso = useCallback(() => dispatch({ tipo: "MARCAR_DUDOSO" }), []);

  // ---- efectos guiados por el paso del ítem -------------------------------
  const item = itemActual(state);
  const mec = mecanicaActual(state);
  const { paso, estado } = state;

  // Audio modelo / pista / consigna: al terminar la locución avanza el guion.
  useEffect(() => {
    if (estado !== "ejercicio" || !item) return;
    let vivo = true;
    const decir = (l: Locucion, fin: { tipo: "MODELO_FIN" | "PISTA_FIN" | "CONSIGNA_FIN" }) =>
      void reproducir(l).then(() => {
        if (vivo) dispatch(fin);
      });

    // Modelo del estímulo: locución humana pregrabada (word_media) o, si no hay,
    // silencio (sin TTS). El guion avanza igual al resolver.
    if (paso === "modelo") decir({ audioRef: item.audioModeloUrl }, { tipo: "MODELO_FIN" });
    if (paso === "pista") {
      // La ventana de 5 s venció con el micrófono abierto: se cierra la captura
      // vacía antes de dar la pista (el niño responde en la segunda ventana). La
      // pista se muestra como texto; no hay audio de pista (sin TTS).
      if (recorder.grabando) void recorder.detener().catch(() => undefined);
      decir({ audioRef: null }, { tipo: "PISTA_FIN" });
    }
    if (paso === "consigna") decir({ audioRef: null }, { tipo: "CONSIGNA_FIN" });
    return () => {
      vivo = false;
    };
  }, [paso, estado, item, recorder]);

  // Micrófono: la denominación lo abre sola (el niño responde espontáneamente);
  // F1 graba con auto-stop; el resto espera el toque del adulto.
  useEffect(() => {
    if (estado !== "ejercicio" || !mec || state.rec !== "idle") return;
    if (paso === "pregunta") void grabarInicio(false);
    else if (paso === "respuesta" && mec.autoStop) void grabarInicio(true);
    // `grabarInicio` es estable por callback; el disparo depende del paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, estado, state.rec, state.itemIdx, state.ejercicioIdx]);

  // Ventana de 5 s de la denominación: sin respuesta → pista, luego → NO_EVOCA.
  useEffect(() => {
    if (estado !== "ejercicio" || paso !== "pregunta") return;
    const t = window.setTimeout(() => dispatch({ tipo: "ESPERA_AGOTADA" }), ESPERA_DENOMINACION_MS);
    return () => window.clearTimeout(t);
  }, [paso, estado, state.itemIdx, state.flags.withHint]);

  // NO_EVOCA: se cierra el micrófono, se registra el ítem y se avanza solo.
  useEffect(() => {
    if (estado !== "ejercicio" || paso !== "no_evoca") return;
    if (recorder.grabando) void recorder.detener().catch(() => undefined);
    const t = window.setTimeout(() => void avanzar(), 1_200);
    return () => window.clearTimeout(t);
  }, [paso, estado, recorder, avanzar]);

  // Suspensión automática ya reflejada en el estado: asegura el POST /suspend.
  useEffect(() => {
    if (estado !== "suspendida" || suspendidoRef.current) return;
    suspendidoRef.current = true;
    void bateriaApi.suspender(bateriaId, state.motivoSuspension ?? "SENAL_ADULTO");
  }, [estado, bateriaId, state.motivoSuspension]);

  // Cierre de la batería: procesa y muestra el aviso de pre-informe.
  useEffect(() => {
    if (estado === "listo_para_procesar") void procesar();
  }, [estado, procesar]);

  return {
    state,
    recorder,
    micOk,
    iniciarEjercicio,
    /** true mientras el POST /start está en vuelo: la UI deshabilita el botón. */
    iniciando,
    siguienteEjercicio,
    grabarInicio,
    grabarFin,
    regrabar,
    reintentarSubida: subir,
    avanzar,
    marcarSinIntento,
    marcarDudoso,
    suspender,
    reanudar,
    distractora: item?.preguntaDistractora ?? textos.distractoraPlaceholder,
    distractoraOk: useCallback(() => dispatch({ tipo: "DISTRACTORA_OK" }), []),
    mecanica: mec ?? MECANICA.F1,
  };
}

/** Precarga best-effort de imagen+audio de un lote de ítems, para render sin
 *  espera por ítem (conexión inestable). No bloquea ni lanza. */
function precargarMedios(
  items: ReadonlyArray<{ imagenUrl: string | null; audioModeloUrl: string | null }>,
): void {
  if (typeof window === "undefined") return;
  for (const it of items) {
    if (it.imagenUrl && typeof Image !== "undefined") {
      new Image().src = it.imagenUrl;
    }
    if (it.audioModeloUrl && typeof Audio !== "undefined") {
      const a = new Audio();
      a.preload = "auto";
      a.src = it.audioModeloUrl;
    }
  }
}

/** Apoyos (pista/distractora) de todos los ítems de la batería, en un viaje. */
async function cargarApoyos(b: BateriaCompletaDTO): Promise<Map<string, PeffApoyo>> {
  const ids = wordIdsDe(b);
  if (ids.length === 0) return new Map();
  try {
    const apoyos = await wordBankApi.peffApoyos(ids);
    return new Map(apoyos.map((a) => [a.wordId, a]));
  } catch {
    // Sin apoyos el reproductor cae a los placeholders; no bloquea la sesión.
    return new Map();
  }
}

export { cargarApoyos };
